import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabaseClient';
import { getAuthUser } from '../../../services/authService';
import { validatePassword } from '../../../lib/passwordValidation';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'hr' | 'recruiter';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'hr' as 'admin' | 'hr' | 'recruiter',
    isActive: true,
    authMethod: 'sso' as 'password' | 'sso'
  });

  useEffect(() => {
    loadCurrentUser();
    loadUsers();
  }, []);

  const loadCurrentUser = async () => {
    const user = await getAuthUser();
    setCurrentUser(user);
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const user = await getAuthUser();

      if (!user || (user.role !== 'admin' && user.role !== 'hr')) {
        setError('Access denied. Admin or HR role required.');
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('entity_id', user.entityId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const formattedUsers = (data || []).map((u: any) => ({
        id: u.id,
        email: u.email,
        name: u.name,
        role: u.role,
        isActive: u.is_active,
        createdAt: u.created_at,
        lastLoginAt: u.last_login_at
      }));

      setUsers(formattedUsers);
      setError('');
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        isActive: user.isActive,
        authMethod: 'sso'
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'hr',
        isActive: true,
        authMethod: 'sso'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'hr',
      isActive: true,
      authMethod: 'sso'
    });
  };

  const validateForm = (): boolean => {
    if (!formData.email || !formData.name) {
      setError('Email and name are required');
      return false;
    }

    // Validate email domain for SSO users - must be @gargash.ae
    if (formData.authMethod === 'sso') {
      const emailDomain = formData.email.toLowerCase().split('@')[1];
      if (emailDomain !== 'gargash.ae') {
        setError('SSO users must have a @gargash.ae email address');
        return false;
      }
    }

    // Password is only required for new users with password auth method
    if (!editingUser && formData.authMethod === 'password' && !formData.password) {
      setError('Password is required for new users with password authentication');
      return false;
    }

    if (formData.password && formData.authMethod === 'password') {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join('. '));
        return false;
      }
    }

    return true;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    try {
      setError('');
      setSuccess('');

      if (formData.authMethod === 'sso') {
        // For SSO users, we create a placeholder in the users table
        // The actual auth will happen when they sign in via SSO
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            email: formData.email,
            name: formData.name,
            role: formData.role,
            entity_id: currentUser.entityId,
            is_active: formData.isActive,
            auth_method: 'sso',
            permissions: []
          });

        if (profileError) {
          throw profileError;
        }

        setSuccess(`SSO User ${formData.name} created successfully! They can now sign in using SSO.`);
      } else {
        // Create auth user via Supabase Auth with password
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.name,
              role: formData.role,
              entity_id: currentUser.entityId
            },
            emailRedirectTo: `${window.location.origin}/admin/login`
          }
        });

        if (authError) throw authError;

        // Wait for trigger to create profile
        await new Promise(resolve => setTimeout(resolve, 2000));

        // If trigger didn't create profile, create manually
        if (authData.user) {
          const { error: profileError } = await supabase
            .from('users')
            .upsert({
              id: authData.user.id,
              email: formData.email,
              name: formData.name,
              role: formData.role,
              entity_id: currentUser.entityId,
              is_active: formData.isActive,
              auth_method: 'password',
              permissions: []
            });

          if (profileError && !profileError.message.includes('duplicate')) {
            throw profileError;
          }
        }

        setSuccess(`User ${formData.name} created successfully!`);
      }

      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    if (!formData.name) {
      setError('Name is required');
      return;
    }

    try {
      setError('');
      setSuccess('');

      // Update user profile
      const { data: updateData, error: updateError } = await supabase
        .from('users')
        .update({
          name: formData.name,
          role: formData.role,
          is_active: formData.isActive
        })
        .eq('id', editingUser.id)
        .select();

      if (updateError) throw updateError;

      // Check if update actually affected rows
      if (!updateData || updateData.length === 0) {
        throw new Error('Update failed. You may not have permission to edit this user.');
      }

      // If password provided, update it
      if (formData.password) {
        const { error: passwordError } = await supabase.auth.admin.updateUserById(
          editingUser.id,
          { password: formData.password }
        );

        // Note: This requires service_role key, may not work with anon key
        if (passwordError) {
          console.warn('Password update requires admin privileges:', passwordError);
        }
      }

      setSuccess(`User ${formData.name} updated successfully!`);
      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const { data: updateData, error: updateError } = await supabase
        .from('users')
        .update({ is_active: !currentStatus })
        .eq('id', userId)
        .select();

      if (updateError) throw updateError;

      // Check if update actually affected rows
      if (!updateData || updateData.length === 0) {
        throw new Error('Status update failed. You may not have permission to modify this user.');
      }

      setSuccess(`User ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      loadUsers();
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      // First check if the user exists and we can see them
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      if (!existingUser) {
        throw new Error('User not found or you do not have permission to delete this user.');
      }

      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (deleteError) throw deleteError;

      // Verify deletion by checking if user still exists
      const { data: checkUser } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      if (checkUser) {
        throw new Error('Delete failed. You may not have permission to delete this user.');
      }

      setSuccess(`User ${userName} deleted successfully`);
      loadUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    }
  };

  const getRoleChip = (role: string) => {
    const roleConfig: Record<string, { color: any; label: string }> = {
      admin: { color: 'error', label: 'Admin' },
      hr: { color: 'primary', label: 'HR' },
      recruiter: { color: 'secondary', label: 'Recruiter' }
    };

    const config = roleConfig[role] || { color: 'default', label: role };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const canEditUser = (user: User) => {
    if (!currentUser) return false;
    // Admin can edit anyone except themselves (to prevent self-lockout)
    // HR can edit recruiters only
    if (currentUser.role === 'admin') {
      return user.id !== currentUser.id;
    }
    if (currentUser.role === 'hr') {
      return user.role === 'recruiter';
    }
    return false;
  };

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'hr')) {
    return (
      <Alert severity="error">
        Access denied. Admin or HR role required to manage users.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add and manage users in your organization
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          Add User
        </Button>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <TableContainer component={Paper} elevation={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add User" to create your first user
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{user.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Disabled'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {canEditUser(user) && (
                        <>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={user.isActive ? 'Disable User' : 'Enable User'}>
                            <IconButton
                              size="small"
                              color={user.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                            >
                              {user.isActive ? <BlockIcon /> : <EnableIcon />}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {user.id === currentUser.id && (
                        <Chip label="You" size="small" color="info" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editingUser}
              required
              helperText={editingUser ? 'Email cannot be changed' : 'Must be a @gargash.ae email address'}
              placeholder="user@gargash.ae"
            />

            {!editingUser && (
              <Alert severity="info">
                This user will sign in using Microsoft SSO (Single Sign-On). No password is required.
              </Alert>
            )}

            <TextField
              fullWidth
              select
              label="Role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              disabled={currentUser?.role === 'hr'} // HR can only create recruiters
            >
              {currentUser?.role === 'admin' && (
                <MenuItem value="admin">Admin</MenuItem>
              )}
              <MenuItem value="hr">HR</MenuItem>
              <MenuItem value="recruiter">Recruiter</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active User"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingUser ? handleUpdateUser : handleCreateUser}
          >
            {editingUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
